import {ElementType, ReactElement} from 'react';

import {BoxProps, Skeleton, Typography, TypographyProps} from '@mui/material';
import Box from '@mui/material/Box';
import {styled} from '@mui/system';

import ReactNative from 'services/react-native';
import {getCurrencySymbol} from 'utils';

import ProductAddToCartButton from './product-add-to-cart-button';
import AddToCartV2 from './product-add-to-cart-button-v2';

const RootWrapper = styled(Box)(() => {
  return {
    position: 'relative',
    width: '100%',
    height: 66,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    '&.isProductCardV2': {
      height: 54
    }
  };
}) as typeof Box;
interface StyledWrapperProps extends BoxProps {
  isLight: boolean;
  textMode?: boolean;
  onlyTextMode?: boolean;
  isProductCardV2?: boolean;
}

const StyledWrapper = styled(Box, {
  shouldForwardProp: (prop) => !(prop == 'isLight' || prop == 'textMode' || prop == 'onlyTextMode')
})<StyledWrapperProps>(({isProductCardV2, isLight, textMode, onlyTextMode, theme}) => ({
  ...(isLight
    ? {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 48,
        ...(textMode
          ? {
              justifyContent: 'center',
              textAlign: 'center'
            }
          : {}),
        ...(onlyTextMode
          ? {
              justifyContent: 'flex-start',
              textAlign: 'center'
            }
          : {})
      }
    : {}),
  ...{
    display: 'flex',
    flexGrow: 1,
    borderRadius: theme.spacing(1),
    background: isLight ? theme.palette.grey[50] : theme.palette.primary.main,
    padding: isLight ? theme.spacing(0, 0.5, 0, 1) : isProductCardV2 ? theme.spacing(0) : theme.spacing(0.5, 0.5, 0, 0),
    minWidth: theme.spacing(18),
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      minWidth: theme.spacing(16)
    }
  }
}));

interface StyledPriceContainerProps extends BoxProps {
  isLeftWrapper: boolean;
}

const StyledPriceContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLeftWrapper'
})<StyledPriceContainerProps>(({isLeftWrapper}) => ({
  ...(isLeftWrapper
    ? {
        flexGrow: 1,
        overflow: 'hidden'
      }
    : {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center'
      })
}));

export const StyledSlabPriceText = styled(Box)(({theme}) => ({
  overflow: 'visible',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  color: theme.palette.common.mds_global_color_white,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: theme.spacing(2),
  width: 'max-content',
  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
    lineHeight: theme.spacing(1.5)
  }
})) as typeof Box;

interface StyledNormalPriceTextProps extends TypographyProps {
  isLight: boolean;
  textMode?: boolean;
  onlyTextMode?: boolean;
  isProductCardV2?: boolean;
}

const StyledNormalPriceText = styled(Typography, {
  shouldForwardProp: (prop) => !(prop == 'isLight' || prop == 'textMode' || prop == 'onlyTextMode')
})<StyledNormalPriceTextProps>(({theme, textMode, isLight, onlyTextMode, isProductCardV2}) => ({
  ...{
    color: isLight ? theme.palette.secondary.main : theme.palette.common.mds_global_color_white,
    fontWeight: 700,
    fontSize: isProductCardV2 ? 16 : 20,
    lineHeight: '20px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '1px',
    ...(textMode
      ? {
          color: theme.palette.grey[300]
        }
      : {}),
    ...(onlyTextMode
      ? {
          color: theme.palette.grey[900]
        }
      : {}),
    ...{
      [theme.breakpoints.down('sm')]: {
        fontSize: 16
      }
    }
  }
}));

export const StyledDiscountContainer = styled(Box)(({theme}) => ({
  gap: theme.spacing(1.5),
  borderRadius: theme.spacing(0, 0.75, 0, 0.75),
  background: theme.palette.secondary.main,
  width: 'fit-content',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(0.4375, 0.5, 0.4875, 1),
  marginTop: theme.spacing(0.5)
})) as typeof Box;

export const StyledDiscountStrikeText = styled(Typography)(({theme}) => ({
  color: theme.palette.common.mds_global_color_white,
  fontWeight: 400,
  textDecorationLine: 'line-through',
  display: 'flex',
  fontSize: theme.spacing(1.5),
  lineHeight: theme.spacing(1.8),
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25),
    lineHeight: theme.spacing(1.5)
  }
})) as typeof Typography;

export const StyledDiscountPercentageValue = styled(Typography)(({theme}) => ({
  color: theme.palette.common.mds_global_color_white,
  textAlign: 'center',
  fontStyle: 'normal',
  fontWeight: 600,
  paddingRight: theme.spacing(0.25),
  lineHeight: theme.spacing(1.75),
  height: 14,
  backgroundColor: theme.palette.primary.main,
  position: 'relative',
  fontSize: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'row',
  borderTopRightRadius: 2,
  borderBottomRightRadius: 2,
  '&::before': {
    position: 'absolute',
    display: 'block',
    width: 0,
    height: 0,
    content: "''",
    borderTop: `8px solid transparent`,
    borderRight: `8px solid ${theme.palette.primary.main}`,
    borderBottom: '8px solid transparent',
    left: -7,
    borderRadius: 2,
    top: -1
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25)
  }
})) as typeof Typography;

interface StyledDiscountWeightedFreshKgText extends TypographyProps {
  isLight?: boolean;
  isProductCardV2?: boolean;
}

const DiscountWeightedFreshKgText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isLight'
})<StyledDiscountWeightedFreshKgText>(({theme, isLight}) => ({
  color: isLight ? theme.palette.common.black : theme.palette.common.white,
  textAlign: 'center',
  fontStyle: 'normal',
  fontWeight: 600,
  paddingLeft: theme.spacing(0.25),
  fontSize: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25)
  }
}));

const StyledCurrencyText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isLight'
})<StyledDiscountWeightedFreshKgText>(({theme}) => ({
  display: 'inline',
  color: theme.palette.common.black,
  fontStyle: 'normal',
  fontWeight: 600,
  margin: '4px 2px 0px 2px',
  fontSize: 12
}));

export const StyledDecimalText = styled(Typography)(({theme}) => ({
  fontWeight: 700,
  display: 'flex',
  fontSize: theme.spacing(1.5)
})) as typeof Typography;

export interface ProductSuggestionProps {
  loading?: boolean;
  title?: string;
  currency: string;
  superText?: string;
  discount?: number;
  discountPercentage?: string | number;
  isAddToCartLoading?: boolean;
  strikeText?: string;
  onlyIcon?: boolean;
  kgText?: string;
  basketItemCount?: number;
  icon?: ReactElement;
  mode: 'compact' | 'primary' | 'text' | 'onlyText';
  component?: ElementType;
  isOutOfStock?: boolean;
  restrictedText?: boolean;
  isProductCardV2?: boolean;
  slabPriceText?: string;
  productId?: string;
  onClick?: () => void;
}

const ProductAddToCart: React.FC<ProductSuggestionProps> = (props) => {
  const {
    loading,
    strikeText,
    discountPercentage,
    onlyIcon,
    title,
    currency,
    kgText,
    basketItemCount,
    isAddToCartLoading,
    mode,
    icon,
    isOutOfStock,
    restrictedText,
    isProductCardV2,
    slabPriceText,
    productId,
    onClick
  } = props;
  const isLight = mode === 'text' || mode == 'compact' || mode == 'onlyText';
  const [whole, decimal] = title ? title.split('.') : ['', ''];
  const hideAddToCartButton = isOutOfStock || restrictedText || false;
  if (loading && !onlyIcon) {
    return <Skeleton variant="rounded" height={67} width={'100%'} />;
  } else if (onlyIcon && !hideAddToCartButton) {
    return (
      <ProductAddToCartButton
        numberValue={basketItemCount}
        icon={icon}
        productId={productId}
        isLoading={isAddToCartLoading}
        mode={mode == 'primary' ? 'light' : 'dark'}
      />
    );
  } else if (isLight) {
    if (mode == 'text') {
      return (
        <RootWrapper data-testid="product-action-root" className={isProductCardV2 ? 'isProductCardV2' : ''}>
          <StyledWrapper isProductCardV2={isProductCardV2} isLight={true} textMode={true}>
            <StyledNormalPriceText
              data-test-id={`${productId}_price`}
              isProductCardV2={isProductCardV2}
              isLight={true}
              textMode={true}
            >
              {title}
            </StyledNormalPriceText>
          </StyledWrapper>
        </RootWrapper>
      );
    }
    if (mode == 'onlyText') {
      return (
        <RootWrapper data-testid="product-action-root" className={isProductCardV2 ? 'isProductCardV2' : ''}>
          <StyledWrapper isProductCardV2={isProductCardV2} isLight={true} textMode={false} onlyTextMode={true}>
            <StyledNormalPriceText
              data-test-id={`${productId}_price`}
              isProductCardV2={isProductCardV2}
              isLight={true}
              textMode={false}
              onlyTextMode={true}
            >
              <StyledCurrencyText isLight={false}>{currency}</StyledCurrencyText>
              {title}
              {kgText && (
                <DiscountWeightedFreshKgText isLight={true} component="span">
                  {kgText}
                </DiscountWeightedFreshKgText>
              )}
            </StyledNormalPriceText>
          </StyledWrapper>
        </RootWrapper>
      );
    }
    return (
      <RootWrapper data-testid="product-action-root" className={isProductCardV2 ? 'isProductCardV2' : ''}>
        <StyledWrapper isProductCardV2={isProductCardV2} isLight={true}>
          <StyledNormalPriceText data-test-id={`${productId}_price`} isProductCardV2={isProductCardV2} isLight={true}>
            <StyledCurrencyText isLight={true}>{getCurrencySymbol()}</StyledCurrencyText>
            {whole}.<StyledDecimalText>{decimal}</StyledDecimalText>
            {kgText && (
              <DiscountWeightedFreshKgText component={'span'} isLight={true}>
                {kgText}
              </DiscountWeightedFreshKgText>
            )}
          </StyledNormalPriceText>
          <StyledPriceContainer isLeftWrapper={false}>
            {!hideAddToCartButton && !isProductCardV2 && (
              <ProductAddToCartButton
                numberValue={basketItemCount}
                disabled={loading}
                mode="dark"
                icon={icon}
                productId={productId}
                onClick={onClick as any}
                isMobile={ReactNative.isReactNativeWebView()}
              />
            )}
          </StyledPriceContainer>
        </StyledWrapper>
      </RootWrapper>
    );
  } else {
    return (
      <RootWrapper data-testid="product-action-root" className={isProductCardV2 ? 'isProductCardV2' : ''}>
        <StyledWrapper isProductCardV2={isProductCardV2} isLight={false}>
          <AddToCartV2
            price={title || ''}
            originalPrice={strikeText || ''}
            discountPercent={discountPercentage ?? ''}
            currency={getCurrencySymbol()}
            slabPriceText={slabPriceText}
            kgText={kgText}
          />
        </StyledWrapper>
      </RootWrapper>
    );
  }
};

ProductAddToCart.displayName = 'MakroUI.ProductAddToCart';

export default ProductAddToCart;
